/** Навык вакансии со знаком технологии — то, что рисует чип. */
export interface IVacancySkillRef {
  id: string;
  name: string;
  slug: string;
  category: string;
  iconUrl: string | null;
}

/**
 * Соответствие навыков студента требованиям вакансии (ТЗ 0073).
 *
 * 🔴 Один объект на карточку, панель деталей и подсветку чипов. Раньше
 * карточка брала число из `matchedSkillCount` ленты (в режиме цели — навыки
 * ПРОФЕССИИ), панель — имена из своего запроса (навыки студента), и обе
 * писали в одну запись кеша: «5 совпало» и «не хватает REST API» на одной
 * вакансии. Считать совпадения в компонентах нельзя — только читать отсюда.
 *
 * `null` — сервер не смог посчитать: интерфейс говорит «не удалось
 * проверить», а не «0 совпадений».
 */
export interface IVacancySkillMatch {
  /** `no_skills` — у студента нет ни одного оценённого навыка: «не хватает» неизвестно. */
  status: 'ok' | 'no_skills';
  matchedIds: string[];
  missing: IVacancySkillRef[];
  /** Упомянуты в CV, но не оценены: не засчитаны и не недостающие. */
  unrated: IVacancySkillRef[];
  matchedCount: number;
  totalCount: number;
}

interface ISkillMatchNode {
  status: string;
  matchedCount: number;
  totalCount: number;
  matchedSkills: readonly { id: string }[];
  missingSkills: readonly { id: string }[];
  unratedSkills: readonly { id: string }[];
}

interface ISkillNode {
  id: string;
  name: string;
  slug?: string | null;
  category?: string | null;
  iconUrl?: string | null;
}

export const toSkillRef = (skill: ISkillNode): IVacancySkillRef => ({
  id: skill.id,
  name: skill.name,
  slug: skill.slug ?? '',
  category: skill.category ?? '',
  iconUrl: skill.iconUrl ?? null,
});

/**
 * Разворачивает `skillMatch` в объекты навыков вакансии.
 *
 * Сервер отдаёт в соответствии только id — имя и знак живут в `skills` той
 * же вакансии, поэтому один и тот же навык не может выглядеть на соседних
 * экранах двумя разными вещами.
 */
export const transformSkillMatch = (
  node: ISkillMatchNode | null | undefined,
  skills: readonly ISkillNode[],
): IVacancySkillMatch | null => {
  if (!node) return null;
  const byId = new Map(skills.map((skill) => [skill.id, toSkillRef(skill)]));
  const refs = (rows: readonly { id: string }[]) =>
    rows.map((row) => byId.get(row.id)).filter((row): row is IVacancySkillRef => Boolean(row));
  return {
    status: node.status === 'no_skills' ? 'no_skills' : 'ok',
    matchedIds: node.matchedSkills.map((row) => row.id),
    missing: refs(node.missingSkills),
    unrated: refs(node.unratedSkills),
    matchedCount: node.matchedCount,
    totalCount: node.totalCount,
  };
};
